import { useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { DialogHeaderActions } from '@@components';

import { ChangeSetClient, ChangeSetViewModel, CreateChangeSetRequest, UpdateChangeSetRequest } from '../../api/api';

type ChangeSetDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess?: () => void;
	mode: 'create' | 'edit';
	changeSetToEdit?: ChangeSetViewModel;
};

const emptyChangeSet: CreateChangeSetRequest = {
	name: '',
	description: '',
};

export default function ChangeSetDialog({ open, setOpen, onSuccess, mode, changeSetToEdit }: ChangeSetDialogProps) {
	const [changeSet, setChangeSet] = useState<CreateChangeSetRequest>(emptyChangeSet);
	const [saving, setSaving] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);

	const toastService = useToastService();
	const client = useApiClient(ChangeSetClient);

	// Initialize form with data when editing
	useEffect(() => {
		if (mode === 'edit' && changeSetToEdit && open) {
			setChangeSet({
				name: changeSetToEdit.name,
				description: changeSetToEdit.description,
			});
		} else if (mode === 'create') {
			setChangeSet(emptyChangeSet);
		}
	}, [mode, changeSetToEdit, open]);

	const handleClose = () => {
		setOpen(false);
		setChangeSet(emptyChangeSet);
		setIsMaximized(false);
	};

	const handleSave = async () => {
		if (!changeSet.name.trim()) {
			toastService.error('Name is required');
			return;
		}

		setSaving(true);
		try {
			if (mode === 'create') {
				await client.create(changeSet);
				toastService.success('ChangeSet created successfully');
			} else if (mode === 'edit' && changeSetToEdit) {
				const updateRequest: UpdateChangeSetRequest = {
					name: changeSet.name,
					description: changeSet.description,
				};
				await client.update(changeSetToEdit.id, updateRequest);
				toastService.success('ChangeSet updated successfully');
			}
			handleClose();
			onSuccess?.();
		} catch (error) {
			console.error('Error saving ChangeSet:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to save ChangeSet: ${errorMessage}`);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMaximized}>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<span style={{ flex: 1 }}>{mode === 'create' ? 'Create ChangeSet' : 'Edit ChangeSet'}</span>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={handleClose}
					closeDisabled={saving}
				/>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="Name"
						required
						value={changeSet.name}
						onChange={(e) => setChangeSet({ ...changeSet, name: e.target.value })}
						fullWidth
						variant="outlined"
						autoFocus
					/>
					<TextField
						label="Description"
						value={changeSet.description || ''}
						onChange={(e) => setChangeSet({ ...changeSet, description: e.target.value })}
						fullWidth
						variant="outlined"
						multiline
						minRows={3}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={saving} loading={saving}>
					{mode === 'create' ? 'Create' : 'Update'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
